import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
    Bell, CheckCircle2, AlertOctagon, Info, ShieldCheck, 
    ShoppingBag, Sparkles, ArrowRight, ArrowLeft, RefreshCw, Trash2
} from 'lucide-react';
import { getProducts, getUserId } from '../api/client';
import { isRTL } from '../utils/translations';
import Spinner from '../components/Spinner';

export default function SellerNotifications() {
    const rtl = isRTL();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);

    const loadNotifications = async () => {
        try {
            setLoading(true);
            const userId = getUserId() || localStorage.getItem('userId');
            const res = await getProducts(0, 100, '', '', '', userId);
            const list = [];

            if (res && res.data) {
                const prods = res.data.content || (Array.isArray(res.data) ? res.data : []);
                prods.forEach(p => {
                    if (p.approvalStatus === 'REJECTED') {
                        list.push({
                            id: `rej-${p.id}`,
                            type: 'REJECTED',
                            title: rtl ? `تنبيه مراجعة: تم رفض "${p.nameAr || p.name}"` : `Action Required: "${p.nameAr || p.name}" was rejected`,
                            message: p.rejectionReason || (rtl ? 'يرجى مراجعة وتوضيح تفاصيل المنتج وجودة الصور.' : 'Please review image quality and descriptions.'),
                            date: p.updatedAt || 'Recent',
                            actionLink: `/seller/products/new?edit=${p.id}`,
                            actionText: rtl ? 'تعديل وإعادة الإرسال' : 'Edit & Resubmit'
                        });
                    } else if (p.approvalStatus === 'APPROVED' || p.active) {
                        list.push({
                            id: `app-${p.id}`,
                            type: 'APPROVED',
                            title: rtl ? `تم اعتماد ونشر "${p.nameAr || p.name}" بنجاح!` : `Approved: "${p.nameAr || p.name}" is now live!`,
                            message: rtl ? 'المنتج متاح الآن للمتسوقين في كافة الأسواق المستهدفة.' : 'Product is now visible to customers in target markets.',
                            date: p.updatedAt || 'Recent',
                            actionLink: `/products/${p.id}`,
                            actionText: rtl ? 'معاينة في المتجر' : 'View Live'
                        });
                    }
                });
            }

            // Add standard platform welcome & operational notification
            list.push({
                id: 'sys-welcome',
                type: 'SYSTEM',
                title: rtl ? 'مرحباً بك في منصة تجار LUXVONO الفاخرة' : 'Welcome to LUXVONO Verified Merchant Hub',
                message: rtl ? 'يمكنك الآن نشر مقاطع الفيديو والمنتجات الفاخرة والتسويق لعملاء 9 دول حول العالم.' : 'You can now publish high-conversion reels and products across 9 global markets.',
                date: 'System'
            });

            setNotifications(list);
        } catch (err) {
            console.error('Failed to load notifications:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadNotifications();
    }, []);

    return (
        <div style={{ maxWidth: 840, marginInline: 'auto' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28, flexWrap: 'wrap', gap: 16 }}>
                <div>
                    <h1 style={{ margin: '0 0 6px 0', fontSize: 22, fontWeight: 800, color: 'var(--seller-text, #111827)' }}>
                        {rtl ? 'إشعارات المراجعة والتحديثات' : 'Moderation & Operations Center'}
                    </h1>
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--seller-muted, #64748b)' }}>
                        {rtl 
                            ? 'إشعارات لحظية حول اعتمادات المنتجات، تنبيهات التعديل، والمبيعات.' 
                            : 'Real-time updates regarding product approvals, editorial feedback, and orders.'}
                    </p>
                </div>

                <button 
                    onClick={loadNotifications}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#ffffff', border: '1px solid var(--seller-border, #e5e7eb)', color: 'var(--seller-text, #111827)', padding: '10px 16px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13, boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}
                >
                    <RefreshCw size={15} />
                    <span>{rtl ? 'تحديث' : 'Refresh'}</span>
                </button>
            </div>

            {/* Notifications List */}
            {loading ? (
                <div style={{ padding: '60px 0', textAlign: 'center' }}>
                    <Spinner />
                </div>
            ) : notifications.length === 0 ? (
                <div style={{ background: 'var(--seller-card, #ffffff)', border: '1px solid var(--seller-border, #e5e7eb)', borderRadius: 14, padding: '50px 20px', textAlign: 'center', color: 'var(--seller-muted, #64748b)', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                    <Bell size={44} style={{ opacity: 0.3, marginBottom: 12, color: '#FE2C55' }} />
                    <p style={{ margin: 0, fontSize: 14 }}>
                        {rtl ? 'لا توجد إشعارات جديدة حالياً.' : 'No new notifications.'}
                    </p>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {notifications.map(item => {
                        const isRejection = item.type === 'REJECTED';
                        const isApproval = item.type === 'APPROVED';

                        return (
                            <div 
                                key={item.id}
                                style={{
                                    background: 'var(--seller-card, #ffffff)',
                                    border: `1px solid ${isRejection ? 'rgba(254,44,85,0.3)' : isApproval ? 'rgba(16,185,129,0.3)' : 'var(--seller-border, #e5e7eb)'}`,
                                    borderRadius: 12,
                                    padding: '18px 20px',
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 16,
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.03)'
                                }}
                            >
                                <div style={{ 
                                    width: 42, 
                                    height: 42, 
                                    borderRadius: 10, 
                                    background: isRejection ? '#fff1f2' : isApproval ? 'rgba(16,185,129,0.12)' : 'rgba(254,44,85,0.08)',
                                    color: isRejection ? '#FE2C55' : isApproval ? '#10b981' : '#FE2C55',
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    flexShrink: 0
                                }}>
                                    {isRejection && <AlertOctagon size={22} />}
                                    {isApproval && <CheckCircle2 size={22} />}
                                    {!isRejection && !isApproval && <Bell size={22} />}
                                </div>

                                <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, flexWrap: 'wrap', gap: 8 }}>
                                        <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: 'var(--seller-text, #111827)' }}>
                                            {item.title}
                                        </h4>
                                        <span style={{ fontSize: 11, color: '#9ca3af' }}>
                                            {item.date}
                                        </span>
                                    </div>

                                    <p style={{ margin: '0 0 10px 0', fontSize: 13, color: isRejection ? '#b91c1c' : 'var(--seller-text, #4b5563)', lineHeight: 1.5 }}>
                                        {item.message}
                                    </p>

                                    {item.actionLink && (
                                        <Link 
                                            to={item.actionLink}
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 6,
                                                background: isRejection ? 'linear-gradient(135deg, #FE2C55 0%, #E01E45 100%)' : '#111827',
                                                color: '#fff',
                                                padding: '7px 16px',
                                                borderRadius: 8,
                                                fontSize: 12,
                                                fontWeight: 700,
                                                textDecoration: 'none',
                                                boxShadow: isRejection ? '0 2px 8px rgba(254,44,85,0.3)' : 'none'
                                            }}
                                        >
                                            <span>{item.actionText}</span>
                                            {rtl ? <ArrowLeft size={13} /> : <ArrowRight size={13} />}
                                        </Link>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
